import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["aside", "condensed", "content", "description", "disableAnimation", "disabled", "error", "hasContentPadding", "hasDegradedPerformance", "hideBullet", "id", "isChecked", "isFirst", "isLast", "isSingleLine", "label", "name", "omitFirstLine", "omitLastLine", "onAnimationEnd", "onChange", "onClickDisabled", "shouldPaddingLeftBullet", "UNSAFE_containerProps", "value", "warning"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import View from '@bubble-ui/primitive-view';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import RadioBullet from '@bubble-ui/atom-radio-bullet';
import Text from '@bubble-ui/primitive-text';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import { useDesignContext } from '@klarna/designtokens';
import { useInteractiveStateCombined } from '@bubble-ui/hook-use-interactive-state';
import useStyleSheet from '@bubble-ui/hook-use-stylesheet';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { defaultDesignTokens } from '../defaultDesignTokens';
import { RadioOptionContent } from '../RadioOptionContent';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { getIds } from './getIds';
var Option = _ref => {
  var {
      aside,
      condensed,
      content,
      description,
      disableAnimation,
      disabled,
      error,
      hasContentPadding,
      hasDegradedPerformance,
      hideBullet,
      id,
      isChecked,
      isFirst,
      isLast,
      isSingleLine = false,
      label,
      name,
      omitFirstLine,
      omitLastLine,
      onAnimationEnd,
      onChange,
      onClickDisabled,
      shouldPaddingLeftBullet,
      UNSAFE_containerProps,
      value,
      warning
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  usePropsSpreadingDeprecationWarning({
    componentName: 'Radio.Option',
    props,
    useInstead: 'UNSAFE_containerProps',
    removalDate: 'v7.0.0'
  });
  var ids = getIds("".concat(name, "-").concat(value));
  var designVersion = useDesignVersion();
  var designContext = useDesignContext(defaultDesignTokens);
  var [interactiveStateCombined, eventHandlers] = useInteractiveStateCombined(_objectSpread({
    disabled
  }, props));
  var {
    articleWrapperStyle,
    asideWrapperStyle,
    contentStyle,
    inlineContentStyle,
    inlineDescriptionStyle,
    inputStyle,
    labelStyle,
    newLineDescriptionStyle,
    paragraphSecondaryCorrectFootprintLightStyle,
    radioWrapperStyle,
    rootStyle,
    spacerVerticalSmallStyle,
    titleStyle
    // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
    // Keep passing all props to the style sheet, even if we don't use them ourselves
  } = useStyleSheet(Option, getDefaultStyleSheet, _objectSpread(_objectSpread({}, props), {}, {
    aside,
    condensed,
    content,
    description,
    designContext,
    designVersion,
    disableAnimation,
    disabled: interactiveStateCombined.disabled,
    error,
    hasContentPadding,
    hasDegradedPerformance,
    hideBullet,
    id,
    isChecked,
    isFirst,
    isLast,
    isSingleLine,
    label,
    name,
    omitFirstLine,
    omitLastLine,
    onAnimationEnd,
    onChange,
    onClickDisabled,
    shouldPaddingLeftBullet,
    UNSAFE_containerProps,
    value,
    warning
  }));
  return /*#__PURE__*/React.createElement(View, _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.container
  }, UNSAFE_containerProps, {
    onClick: e => {
      if (onClickDisabled && interactiveStateCombined.disabled) {
        onClickDisabled(value, e);
      }
    },
    onMouseOver: eventHandlers.onMouseOver,
    onMouseOut: eventHandlers.onMouseOut,
    onMouseDown: eventHandlers.onMouseDown,
    onMouseUp: eventHandlers.onMouseUp,
    style: rootStyle
  }, props), /*#__PURE__*/React.createElement("label", {
    id: ids === null || ids === void 0 ? void 0 : ids.label,
    htmlFor: ids === null || ids === void 0 ? void 0 : ids.input,
    style: labelStyle
  }, /*#__PURE__*/React.createElement("input", {
    style: inputStyle,
    id: ids === null || ids === void 0 ? void 0 : ids.input,
    name: name,
    type: "radio",
    checked: isChecked,
    value: value,
    disabled: interactiveStateCombined.disabled,
    onChange: e => {
      if (onChange && !isChecked) {
        onChange(value, e);
      }
    },
    onFocus: eventHandlers.onFocus,
    onBlur: eventHandlers.onBlur
  }), !hideBullet && /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.radioWrapper,
    style: radioWrapperStyle
  }, /*#__PURE__*/React.createElement(RadioBullet, _extends({
    excludeInput: true,
    id: ids === null || ids === void 0 ? void 0 : ids.radioMark,
    checked: isChecked,
    error: error,
    warning: warning
  }, interactiveStateCombined))), /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.articleWrapper,
    style: articleWrapperStyle
  }, /*#__PURE__*/React.createElement(Text, {
    id: ids === null || ids === void 0 ? void 0 : ids.description,
    style: titleStyle
  }, label, ' ', description && condensed && /*#__PURE__*/React.createElement(Text, {
    style: inlineContentStyle
  }, "\xA0 ", description)), description && isSingleLine && /*#__PURE__*/React.createElement(Text, {
    style: inlineDescriptionStyle,
    id: ids === null || ids === void 0 ? void 0 : ids.description
  }, description), description && !condensed && !isSingleLine && /*#__PURE__*/React.createElement(View, {
    style: newLineDescriptionStyle
  }, /*#__PURE__*/React.createElement(View, {
    style: spacerVerticalSmallStyle
  }), /*#__PURE__*/React.createElement(Text, {
    tag: "paragraph",
    style: paragraphSecondaryCorrectFootprintLightStyle,
    id: ids === null || ids === void 0 ? void 0 : ids.description
  }, description)), hasDegradedPerformance && aside && /*#__PURE__*/React.createElement(View, null, /*#__PURE__*/React.createElement(View, {
    style: spacerVerticalSmallStyle
  }), aside)), !hasDegradedPerformance && /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.asideWrapper,
    style: asideWrapperStyle
  }, aside)), content && /*#__PURE__*/React.createElement(RadioOptionContent, {
    onAnimationEnd: onAnimationEnd,
    id: ids === null || ids === void 0 ? void 0 : ids.radioOptionContent,
    isExpanded: isChecked,
    disableAnimation: disableAnimation
  }, /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.content,
    style: contentStyle
  }, content)));
};
Option.displayName = 'Radio.RadioOption';
export var RadioOption = withProvisionalOverrideFromContext(Option);
//# sourceMappingURL=index.js.map